// src/pages/team/MembersPage.tsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Trash2, Sliders, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient } from '@/lib/api';
import { cn } from '@/lib/utils';
import Button from '@/components/ui/Button';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { EmptyState } from '@/components/ui/EmptyState';
import { Table, THead, TBody, Tr, Th, Td, TableSkeleton } from '@/components/ui/Table';
import { fieldControl, fieldControlSm } from '@/components/ui/fieldStyles';

interface Member {
  user_id: string;
  email: string;
  budget_cap: number;
  rate_limit_rpm: number;
  joined_at: number;
  role: string | null;
}

const ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: 'owner', label: 'Owner' },
  { value: 'workspace_admin', label: 'Admin' },
  { value: 'developer', label: 'Developer' },
  { value: 'billing', label: 'Billing' },
];

function roleLabel(role: string | null): string {
  return ROLE_OPTIONS.find((r) => r.value === role)?.label ?? 'No role';
}

const MembersPage: React.FC = () => {
  const qc = useQueryClient();
  const [inviteEmail, setInviteEmail] = useState('');

  const { data: members = [], isLoading } = useQuery<Member[]>({
    queryKey: ['team-members'],
    queryFn: () => apiClient.get('/team/members').then(r => r.data),
  });

  const invite = useMutation({
    mutationFn: (email: string) => apiClient.post('/team/members/invite', { email }),
    onSuccess: () => { toast.success('Invitation sent'); qc.invalidateQueries({ queryKey: ['team-members'] }); setInviteEmail(''); },
    onError: (e: any) => toast.error(e.response?.data?.error ?? 'Invite failed'),
  });

  const remove = useMutation({
    mutationFn: (userId: string) => apiClient.delete(`/team/members/${userId}`),
    onSuccess: () => { toast.success('Member removed'); qc.invalidateQueries({ queryKey: ['team-members'] }); },
    onError: (e: any) => toast.error(e.response?.data?.error ?? 'Remove failed'),
  });

  const updateCaps = useMutation({
    mutationFn: ({ userId, budgetCap, rateLimit }: { userId: string; budgetCap: number; rateLimit: number }) =>
      apiClient.patch(`/team/members/${userId}`, { budget_cap: budgetCap, rate_limit_rpm: rateLimit }),
    onSuccess: () => { toast.success('Limits updated'); qc.invalidateQueries({ queryKey: ['team-members'] }); },
  });

  const updateRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      apiClient.post(`/team/members/${userId}/role`, { role }),
    onSuccess: () => { toast.success('Role updated'); qc.invalidateQueries({ queryKey: ['team-members'] }); },
    onError: (e: any) => toast.error(e.response?.data?.error ?? 'Role update failed'),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Team"
        description="Invite teammates and manage their roles."
        actions={
          <Link to="/roles" className="text-sm font-medium text-accent-text hover:underline">
            View role permissions
          </Link>
        }
      />

      <Panel title="Invite a teammate">
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            type="email"
            aria-label="Teammate's email"
            className={cn(fieldControl, 'sm:flex-1')}
            placeholder="colleague@company.com"
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
          />
          <Button
            className="h-11"
            leftIcon={<UserPlus className="h-4 w-4" />}
            disabled={!inviteEmail || invite.isPending}
            onClick={() => invite.mutate(inviteEmail)}
          >
            Invite
          </Button>
        </div>
      </Panel>

      <Panel title="Members" bodyClassName="px-0 pb-2">
        {isLoading ? (
          <TableSkeleton rows={3} />
        ) : members.length === 0 ? (
          <EmptyState icon={<Users />} title="No members yet." description="Invite someone above." />
        ) : (
          <Table minWidth={760}>
            <THead>
              <tr>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th align="right">Budget cap (credits)</Th>
                <Th align="right">Rate limit (rpm)</Th>
                <Th>Joined</Th>
                <Th><span className="sr-only">Actions</span></Th>
              </tr>
            </THead>
            <TBody>
              {members.map(m => (
                <MemberRow
                  key={m.user_id}
                  member={m}
                  onSave={(budgetCap, rateLimit) => updateCaps.mutate({ userId: m.user_id, budgetCap, rateLimit })}
                  onRemove={() => remove.mutate(m.user_id)}
                  onRoleChange={(role) => updateRole.mutate({ userId: m.user_id, role })}
                />
              ))}
            </TBody>
          </Table>
        )}
      </Panel>
    </div>
  );
};

const MemberRow: React.FC<{
  member: Member;
  onSave: (budgetCap: number, rateLimit: number) => void;
  onRemove: () => void;
  onRoleChange: (role: string) => void;
}> = ({ member, onSave, onRemove, onRoleChange }) => {
  const [budgetCap, setBudgetCap] = useState(member.budget_cap);
  const [rateLimit, setRateLimit] = useState(member.rate_limit_rpm);
  const [editing, setEditing] = useState(false);

  return (
    <Tr>
      <Td>{member.email}</Td>
      <Td>
        <select
          value={member.role ?? ''}
          onChange={(e) => onRoleChange(e.target.value)}
          aria-label={`Role for ${member.email}`}
          className={cn(fieldControlSm, 'w-36')}
        >
          {member.role === null && <option value="" disabled>{roleLabel(null)}</option>}
          {ROLE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </Td>
      <Td align="right" mono>
        {editing
          ? <input type="number" aria-label="Budget cap (credits)" className={cn(fieldControlSm, 'ml-auto w-28 text-right font-mono')} value={budgetCap} onChange={e => setBudgetCap(+e.target.value)} />
          : budgetCap.toLocaleString()}
      </Td>
      <Td align="right" mono>
        {editing
          ? <input type="number" aria-label="Rate limit (rpm)" className={cn(fieldControlSm, 'ml-auto w-24 text-right font-mono')} value={rateLimit} onChange={e => setRateLimit(+e.target.value)} />
          : rateLimit}
      </Td>
      <Td muted className="whitespace-nowrap">{new Date(member.joined_at * 1000).toLocaleDateString()}</Td>
      <Td align="right">
        <div className="flex justify-end gap-1">
          {editing ? (
            <>
              <Button size="sm" variant="ghost" className="text-accent-text" onClick={() => { onSave(budgetCap, rateLimit); setEditing(false); }}>Save</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="ghost" title="Edit limits" aria-label="Edit limits" onClick={() => setEditing(true)}><Sliders className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" title="Remove member" aria-label="Remove member" className="hover:text-error" onClick={onRemove}><Trash2 className="h-4 w-4" /></Button>
            </>
          )}
        </div>
      </Td>
    </Tr>
  );
};

export default MembersPage;
